import { memo, useEffect, useState } from 'react'
import CustomizeModal from '../components/CustomizeModal'
import Checkbox from '../components/form/Checkbox';
import NightsList from '../components/NightsList';
import RangeSlider from '../components/RangeSlider';
import { t } from '../../../utilities/translator';
import { pushEvent } from '../../../utilities/analytics';
import { CUSTOMIZE_FILTERS_SAVED } from '../../../constants/analyticEvents';
import { CUSTOMIZE, DATE_TYPES } from '../../../constants/constants';
import { toggleCustomizePanel } from '../customizeButtonSlice';
import { intervalToDuration } from 'date-fns';
import abTesting from '../../../services/abTesting';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';

type FiltersProps = {
  show: boolean;
  selected: any;
  budgetSelection: number[];
  nightsSelection: number[];
  onSave: (data: any) => void;
  whenSelection: {
    type: string;
    startDate: Date | null;
    endDate: Date | null;
  };
  handleClosePanel: () => void;
  onClose: () => void;
}

const Filters = ({
  show,
  selected,
  budgetSelection,
  nightsSelection,
  onSave,
  whenSelection,
  handleClosePanel,
  onClose,
}: FiltersProps) => {
  const dispatch = useAppDispatch();
  const { initial: budgetRange } = useAppSelector(state => state.mainFilter.searchEngine.budget)
  const [localState, setLocalState] = useState(selected);
  const [selectedNights, setSelectedNights] = useState(nightsSelection);
  const [selectedBudget, setSelectedBudget] = useState(budgetSelection);
  const { activePanel } = useAppSelector(state => state.customize);

  const { type, startDate, endDate } = whenSelection;

  useEffect(() => {
    setLocalState(selected);
    setSelectedBudget(budgetSelection);
    setSelectedNights(nightsSelection);
  }, [selected, nightsSelection, budgetSelection])

  const handleNightClick = (night) => {
    if (selectedNights.includes(night)) {
      setSelectedNights(selectedNights.filter(selectedNight => selectedNight !== night));
    } else {
      setSelectedNights([...selectedNights, night]);
    }
  }

  const handleCheckbox = (filterName, value) => {
    if (filterName === 'duration') {
      return (duration) => {
        setLocalState({
          ...localState,
          [filterName]: duration,
        });
      };
    }
    if (filterName === 'flex') {
      return () => {
        const newVal = {
          ...localState,
          flex: {
            ...localState.flex,
            active: value
          }
        };
        setLocalState(newVal);
      };
    }
    if (filterName === 'sort') {
      return () => {
        setLocalState({
          ...localState,
          [filterName]: localState[filterName].map((item, i) =>
            value === i ? { ...item, active: true } : { ...item, active: false }
          ),
        });
      };
    }
    return () => {
      setLocalState({
        ...localState,
        [filterName]: localState[filterName].map((item) =>
          value === item.value ? { ...item, active: !item.active } : item
        ),
      });
    };
  };

  const handleSave = () => {
    onSave({
      filters: localState,
      nights: selectedNights,
      budget: selectedBudget,
    });
    pushEvent(CUSTOMIZE_FILTERS_SAVED);
    if (activePanel === CUSTOMIZE.FILTERS) {
      dispatch(toggleCustomizePanel(null));
    }
  }

  const handleCloseFilters = () => {
    setLocalState(selected);
    setSelectedBudget(budgetSelection);
    setSelectedNights(nightsSelection);
    onClose();
    if (activePanel === CUSTOMIZE.FILTERS) {
      dispatch(toggleCustomizePanel(null));
      handleClosePanel();
    }
  }

  const handleClear = () => {
    const clearFilters = {
      ...localState,
      board: localState.board.map(i => ({ ...i, active: false })),
      facilities: localState.facilities.map(i => ({ ...i, active: false })),
      freeCancellation: false,
      sort: localState.sort.map(i => ({ ...i, active: i.label === 'best' })),
      stars: localState.stars.map(i => ({ ...i, active: false })),
      stops: localState.stops.map(i => ({ ...i, active: false })),
      luggage: localState.luggage.map(i => ({ ...i, active: false })),
      flex: { ...localState.flex, active: false },
    }
    setLocalState(clearFilters)
    onSave({
      filters: clearFilters,
      nights: [],
      budget: [budgetRange.min, budgetRange.max]
    })
    setSelectedBudget([budgetRange.min, budgetRange.max])
    if (activePanel === CUSTOMIZE.FILTERS) {
      dispatch(toggleCustomizePanel(null));
      handleClosePanel();
    }
  }

  let daysInterval = 20;

  if (type === 'range' && startDate && endDate) {
    daysInterval = intervalToDuration({
      start: startDate,
      end: endDate,
    }).days;
  }

  return (
    <CustomizeModal show={show} close={handleCloseFilters} slideDirection="vertical">
      <CustomizeModal.Header close={handleCloseFilters} />
      <CustomizeModal.Body>
        <div className="text-xl font-semibold text-start">{t('customize.filters')}</div>

        <div className="flex flex-col gap-10">
          <div>
            <div className="mb-5 label-large text-start font-semibold">{t("mainFilter.total_price")}</div>
            <RangeSlider
              initial={budgetRange}
              selected={selectedBudget}
              onChange={(value) => setSelectedBudget(value)}
              sliderColors={["#E7E7E7", "#212121", "#E7E7E7"]}
            />
          </div>
          <div>
            <div className="mb-5 label-large text-start font-semibold">{t('secondaryFilter.property_class')}</div>
            <div className="flex flex-col-reverse gap-3">
              {localState.stars.map(item => (
                <Checkbox
                  label={`${item.label} ${t('secondaryFilter.stars_plural', { count: item.label })}`}
                  checked={item.active}
                  onChange={handleCheckbox('stars', item.value)}
                />
              ))}
            </div>
          </div>
          {whenSelection.type !== DATE_TYPES.SPECIFIC && (
            <div>
              <NightsList
                onChange={handleNightClick}
                selected={selectedNights}
                showWeekendsOnly={false}
                daysInterval={daysInterval}
                variant="gray"
              />
            </div>
          )}
          <div>
            <div className="mb-5 label-large text-start font-semibold">{t('secondaryFilter.Board_type')}</div>
            <div className="flex flex-col gap-3">
              {localState.board.map(item => (
                <Checkbox
                  label={item.label}
                  checked={item.active}
                  onChange={handleCheckbox('board', item.value)}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="mb-5 label-large text-start font-semibold">{t('secondaryFilter.Stops')}</div>
            <div className="flex flex-col gap-3">
              {localState.stops.map(item => (
                <Checkbox
                  label={item.label}
                  checked={item.active}
                  onChange={handleCheckbox('stops', item.value)}
                />
              ))}
            </div>
          </div>
          {abTesting.moveMultiRates && (
            <div>
              <div className="mb-5 label-large text-start font-semibold">{t('secondaryFilter.luggage')}</div>
              <div className="flex flex-col gap-3">
                {localState.luggage.map(item => (
                  <Checkbox
                    label={item.label}
                    checked={item.active}
                    onChange={handleCheckbox('luggage', item.value)}
                  />
                ))}
              </div>
            </div>
          )}
          {abTesting.moveMultiRates && (
            <div>
              <div className="mb-5 label-large text-start font-semibold">{t('secondaryFilter.cancellationFlexibility.title')}</div>
              <div>
                <Checkbox
                  label={t("secondaryFilter.refundableWithFlex")}
                  checked={localState.flex.active}
                  onChange={handleCheckbox('flex', !localState.flex.active)}
                />
              </div>
            </div>
          )}
          <div>
            <div className="mb-5 label-large text-start font-semibold">{t('secondaryFilter.Ameneties')}</div>
            <div className="flex flex-col gap-3">
              {localState.facilities.map(item => (
                <Checkbox
                  label={item.label.name}
                  checked={item.active}
                  onChange={handleCheckbox('facilities', item.value)}
                />
              ))}
            </div>
          </div>
        </div>
      </CustomizeModal.Body>

      <CustomizeModal.Footer
        close={handleCloseFilters}
        onSave={handleSave}
        onClear={handleClear}
      />
    </CustomizeModal>
  )
}

export default memo(Filters)
